import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import styles from '../AsnUpdatesLive/AsnUpdatesLive.module.css';
import { usePlantAndLinesStore } from '../../providers/PlantAndLinesStore/PlantAndLinesStore';
import { buildErrorMessage } from '../../apis/calls';
import {
  AGGREGATES,
  AGGREGATE_STATUS,
  API_RESOURCE_URLS,
  DATE_FORMAT,
  SCREEN_REFRESH_INTERVAL_TIME_MS,
} from '../../constants';
import { API } from '../../apis/api';
import { usePopupManager, } from '../../providers/PopupManager/PopupManager';
import AgsnUpdatesLiveToolbar from '../AgsnUpdatesLiveToolbar/AgsnUpdatesLiveToolbar';
import AsnUpdatesTable from '../AsnUpdatesTable/AsnUpdatesTable';
import AsnUpdateFooter from '../AsnUpdateFooter/AsnUpdateFooter';
import {
  formatDataToIncludeColour,
  getAggregateStatusAndModifiedAtAndComment,
} from '../../utils/tableUtils';

const AgsnUpdatesLive = () => {
  const { showInternalError } = usePopupManager();
  const [plant] = usePlantAndLinesStore().plantState;
  const [aggregateLine] = usePlantAndLinesStore().aggregateLineState;
  const [selectedLine, setSelectedLine] = useState([]);
  const [activeAsnDate, setActiveAsnDate] = useState(null);
  const [selectedOptionalColNames, setSelectedOptionalColNames] = useState([]);
  const history = useHistory();
  const [aggregate, setAggregate] = useState(() => {
    return history.location.state?.aggregate || localStorage.getItem('aggregate');
  });

  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
  });
  

  if (selectedLine === '') {
    setSelectedLine(aggregateLine);
  }

  const statusFieldByMaterialGroup = new Map([
    [AGGREGATES.FRAME, 'frameStatuses'],
    [AGGREGATES.FRONT_AXLE, 'frontAxleStatuses'],
    [AGGREGATES.REAR_AXLE, 'rearAxleStatuses'],
    [AGGREGATES.CAB, 'cabStatuses'],
    [AGGREGATES.ENGINE, 'engineStatuses'],
    [AGGREGATES.MATERIAL, 'scmMaterialStatuses'],
    [AGGREGATES.GEAR_BOX, 'gearboxStatuses'],
    [AGGREGATES.PAINT, 'paintStatuses'],

  ]);

  const addAggregateStatusFlagsAndSortAndFormatForRender = asns => {
    if (asns.length === 0) return [];
    const rows = asns.map(asn => {
      const updatedMaterialGroupClusters = asn.materialGroupClusters.map(
        ({ name, materials }) => ({
          name,
          materials: materials.map(material =>
            getAggregateStatusAndModifiedAtAndComment(material),
          ),
        }),
      );

      const hasUnAvailableAggregates = updatedMaterialGroupClusters
        .map(({ materials }) => materials[0])
        .some(
          ({ aggregateStatus }) =>
            aggregateStatus === AGGREGATE_STATUS.LINE_OUT,
        );

      const aggregateStatuses = updatedMaterialGroupClusters.reduce(
        (acc, materialGroup) => {
          const aggregateStatus = materialGroup.materials.map(
            ({ aggregateStatus, material,aggregatePlanningReason }) => ({ status: aggregateStatus, material,aggregatePlanningReason }),
          );
          const obj = {};
          if (statusFieldByMaterialGroup.has(materialGroup.name)) {
            // enabling multiple quantities data for front and rear axle
            
            
            if (
              materialGroup.name === AGGREGATES.FRONT_AXLE ||
              materialGroup.name === AGGREGATES.REAR_AXLE
            )
              obj[
                statusFieldByMaterialGroup.get(materialGroup.name)
              ] = aggregateStatus;
            else
              obj[statusFieldByMaterialGroup.get(materialGroup.name)] = [
                aggregateStatus[0],
              ];
              
          }
          return { ...acc, ...obj };
        },
        {},
      );

      return {
        ...asn,
        ...aggregateStatuses,
        materialGroupClusters: [...updatedMaterialGroupClusters],
        hasUnAvailableAggregates,
      };
    });
    rows.sort((a, b) => a.asnId.localeCompare(b.asnId));
    const rowsWithIndex = rows.map((record, index) => {
      return { rowIndex: index + 1, ...record };
    });
    return rowsWithIndex;
  };

  const loadVcAsnForDateAndPlantAndLine = async (
    date = activeAsnDate,
    selectedPlantValue = plant.value,
    selectedaggregatelinevalue = aggregateLine.value,
  ) => {
    if (!date)
      return setRowsData({
        loading: false,
        data: [],
        filteredData: [],
      });
    try {
      setRowsData(prevState => ({ ...prevState, loading: true }));
      const dateString = date.format(DATE_FORMAT.ISO);
      
        const chassisAsnsForADay = await API.get(
        API_RESOURCE_URLS.getAgsnOverview(
          selectedPlantValue,
          selectedaggregatelinevalue,
          dateString,
        ),
      );
    
      let chassisOverViewWithChildMaterialStatus = [];
      let outerLoopCount = 0;
      const maxPayloadLength = 250;
      for (
        let outLoop = 0;
        outLoop < Math.ceil(chassisAsnsForADay.data?.length / maxPayloadLength);
        outLoop++
      ) {
        let innerLoopPayloadLenght = 0;

        if (outLoop > 0) {
          innerLoopPayloadLenght = outLoop * maxPayloadLength;
        }

        let asnIds = [];

        for (
          let asnCount = innerLoopPayloadLenght;
          asnCount < chassisAsnsForADay.data.length;
          asnCount++
        ) {
          if (outerLoopCount < 250) {
            if (asnIds.length <= 0)
              asnIds.push(chassisAsnsForADay.data[asnCount].asnId);
            else asnIds.push(chassisAsnsForADay.data[asnCount].asnId);
            outerLoopCount++;
          } else if (outLoop > 0) {
            if (
              outerLoopCount <
              maxPayloadLength * outLoop + maxPayloadLength
            ) {
              if (asnIds.length <= 0)
                asnIds.push(chassisAsnsForADay.data[asnCount].asnId);
              else asnIds.push(chassisAsnsForADay.data[asnCount].asnId);
              outerLoopCount++;
            }
          } else {
            outerLoopCount = 0;
            break;
          }
        }
          const childMaterialStatusesForADay = await API.get(
            API_RESOURCE_URLS.aggregateMaterialstatus(
              selectedPlantValue,
              selectedaggregatelinevalue,
              asnIds,
            ),
          );
        for (
          let childMaterialCount = innerLoopPayloadLenght;
          childMaterialCount < chassisAsnsForADay.data.length;
          childMaterialCount++
        ) {
          if (childMaterialCount < 250) {
            const findChildMaterialAsn = childMaterialStatusesForADay.data.find(
              childMaterialDetails => {
                return (
                  chassisAsnsForADay.data[childMaterialCount].asnId ===
                  childMaterialDetails.asnId
                );
              },
            );

            let parentChildData = {
              ...chassisAsnsForADay.data[childMaterialCount],
              materialGroupClusters:
                findChildMaterialAsn?.materialGroupClusters || [],
            };

            chassisOverViewWithChildMaterialStatus.push(parentChildData);
          }

          if (
            outLoop > 0 &&
            childMaterialCount < maxPayloadLength * outLoop + maxPayloadLength
          ) {
            const findChildMaterialAsn = childMaterialStatusesForADay.data.find(
              childMaterialDetails => {
                return (
                  chassisAsnsForADay.data[childMaterialCount].asnId ===
                  childMaterialDetails.asnId
                );
              },
            );

            let parentChildData = {
              ...chassisAsnsForADay.data[childMaterialCount],
              materialGroupClusters:
                findChildMaterialAsn?.materialGroupClusters || [],
            };

            chassisOverViewWithChildMaterialStatus.push(parentChildData);
          }
        }
      }

      const processedData = addAggregateStatusFlagsAndSortAndFormatForRender(
        chassisOverViewWithChildMaterialStatus,
      );
      const data = formatDataToIncludeColour(processedData);
      setRowsData({
        loading: false,
        data,
      });
    } catch (err) {
      console.error(buildErrorMessage(err));
      showInternalError({ error: err });
      setRowsData(prevState => ({ ...prevState, loading: false }));
    }
  };

  useEffect(() => {
    if (history.location.state?.aggregate) {
      setAggregate(history.location.state.aggregate);
      localStorage.setItem('aggregate', history.location.state.aggregate);
    }
  }, [history.location.state]);

  useEffect(() => {
    if (aggregate) {
      localStorage.setItem('aggregate', aggregate);
    }
  }, [aggregate]);
  useEffect(() => {
    loadVcAsnForDateAndPlantAndLine(activeAsnDate, plant.value, aggregateLine.value);

    // Set up interval to refresh data every 5 seconds
    const intervalId = setInterval(
      loadVcAsnForDateAndPlantAndLine,
      SCREEN_REFRESH_INTERVAL_TIME_MS,
    );

    // Clean up the interval on component unmount
    return () => {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAsnDate, plant.value, selectedLine]);

  return (
    <div className={styles.display}>
      <div className={styles.header}>
        <AgsnUpdatesLiveToolbar
          activeDate={activeAsnDate}
          setActiveDate={setActiveAsnDate}
          plant={plant}
          line={aggregateLine}
          selectedOptionalColNames={selectedOptionalColNames}
          agsn={aggregate}
          handleColumnSelection={selection =>{
            setSelectedOptionalColNames(selection)}
          }
        />
      </div>
      <div className={styles.tableArea}>
        <AsnUpdatesTable
          rows={rowsData.data}
          selectedOptionalColNames={selectedOptionalColNames}
          agsn={aggregate}
        />
      </div>
      <div className={styles.footer}>
        <AsnUpdateFooter asns={rowsData.data} activeDate={activeAsnDate} />
      </div>
    </div>
  );
};

AgsnUpdatesLive.propTypes = {};

export default AgsnUpdatesLive;
